import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { cloneDeep, isEqual } from 'lodash'

import { AddBreak } from './components/AddBreak'

import useToasterHelper from '../../../../../../helpers/ToasterHelper'
import { useEventRiderTeamMembers } from '../../../../../../hooks/useEventRiderTeamMembers'
// import { handleDragEnd, onScratch } from './data/order-of-go-utils'
import FirestoreService from '../../../../../../services/firestoreService'

import { selectedEvent, setSelectedEventKey } from '../../../../../../store/events/eventsSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'

import { IBreak, IBreakRegistrationByDay } from '../../../../../../models/events/event.interface'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'

import { CONST } from '../../../../../../const/const'
import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { IManageOption } from '../../types/IManageOption'
import { DraggableRiderRow } from './components/DraggableRiderRow'
import { OrderOfGoRiderTableHeader } from './components/OrderOfGoRiderTableHeader'
import { getRiderColumns } from './data/columns-rider'
import {
  calculateRiderClashes,
  hasClash,
  recalculateAllRideTimes,
} from './data/order-of-go-rider-utils'
import { sortByOrder, sortByOutOfTurn } from '../../data/operations-tab-utils'
import { httpService } from '../../../../../../services/httpService'

interface OrderOfGoTableByRiderProps {
  selectedDay: IManageOption
  eventId: string
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  registrationsByDay: IRegistrationByDayInterface[] | null
  registrationsGroupedByDay: { [key: string]: IBreakRegistrationByDay[] }
  gapTime: string
  onClashCountChange: (clashCount: number) => void
  isEditMode: boolean
  onToggleEditMode: () => void
  searchValue: string
}

export const OrderOfGoTableByRider = ({
  selectedDay,
  eventId,
  setRegistrationsByDay,
  registrationsByDay,
  registrationsGroupedByDay,
  gapTime,
  onClashCountChange,
  isEditMode,
  onToggleEditMode,
  searchValue,
}: OrderOfGoTableByRiderProps) => {
  const dispatch = useAppDispatch()
  const toastFunctions = useToasterHelper()
  const { eventTeamMembers, eventTeamMembersLoading } = useEventRiderTeamMembers(eventId)

  const [isDragging, setIsDragging] = useState(false)
  const [stagedData, setStagedData] = useState<IBreakRegistrationByDay[]>([])
  const [clashedIds, setClashedIds] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const currentEvent = useAppSelector(selectedEvent)

  const originalData = useMemo(() => {
    return selectedDay ? registrationsGroupedByDay[selectedDay.value] || [] : []
  }, [selectedDay, registrationsGroupedByDay])

  useEffect(() => {
    if (isEditMode) {
      setStagedData((prev) => {
        const sameLength = prev.length === originalData.length
        const sameIds = sameLength && prev.every((item, i) => item.id === originalData[i]?.id)
        if (sameIds) return prev
        return originalData.map((item) => ({ ...item }))
      })
    } else {
      setStagedData((prev) => (prev.length > 0 ? [] : prev))
    }
  }, [isEditMode, originalData])

  const registrationsToDisplay = useMemo(() => {
    if (!isEditMode) return originalData
    return stagedData.length > 0 ? stagedData : originalData
  }, [originalData, stagedData, isEditMode])

  const clashData = useMemo(() => {
    if (!isEditMode) return []
    return calculateRiderClashes(registrationsToDisplay, gapTime)
  }, [isEditMode, registrationsToDisplay, gapTime])

  useEffect(() => {
    setClashedIds(clashData)
    onClashCountChange?.(clashData.length)
  }, [clashData, onClashCountChange])

  const updateFirstRiderTimeStaged = useCallback(
    (newTime: string, riderId: string) => {
      if (!isEditMode) return

      setStagedData((currentStagedData) => {
        const firstRider = currentStagedData.find((item) => !item.isBreakDrag && !item.isOutOfTurn)

        if (firstRider?.id !== riderId) {
          console.error(`Expected first rider ${firstRider?.id}, but ${riderId} called this func`)
          return currentStagedData
        }

        return recalculateAllRideTimes(currentStagedData, newTime)
      })
    },
    [isEditMode]
  )

  const updateOutOfTurnRiderTimeStaged = useCallback(
    (newTime: string, riderId: string) => {
      if (!isEditMode) return

      setStagedData((currentStagedData) => {
        const riderIndex = currentStagedData.findIndex(
          (item) => item.id === riderId && item.isOutOfTurn
        )

        if (riderIndex === -1) {
          console.error(`Out-of-turn rider with id ${riderId} not found`)
          return currentStagedData
        }

        const updatedData = [...currentStagedData]
        updatedData[riderIndex] = {
          ...updatedData[riderIndex],
          rideTime: newTime,
        }

        return updatedData
      })
    },
    [isEditMode]
  )

  const updateBreakTimeStaged = useCallback(
    (h: string, m: string, s: string, breakId: string) => {
      if (!isEditMode) return

      setStagedData((currentStagedData) => {
        const newBreakTime = `${h}:${m}:${s}`
        const newData = cloneDeep(currentStagedData)
        const breakIndex = newData.findIndex((item) => item.id === breakId && item.isBreakDrag)

        if (breakIndex < 0) {
          toastFunctions.error({ message: `Break with id ${breakId} not found in staged data` })
          return currentStagedData
        }

        newData[breakIndex] = {
          ...newData[breakIndex],
          time: newBreakTime,
        }

        const firstRider = newData.find(
          (item) => !item.isBreakDrag && !item.isOutOfTurn && item.order === 1
        )

        if (firstRider?.rideTime) {
          return recalculateAllRideTimes(newData, firstRider.rideTime)
        }

        return newData
      })
    },
    [isEditMode]
  )

  const deleteBreakStaged = useCallback(
    (breakId: string) => {
      if (!isEditMode) return
      setStagedData((currentStagedData) => {
        const newData = [...currentStagedData]
        const breakIndex = newData.findIndex((item) => item.id === breakId && item.isBreakDrag)

        if (breakIndex < 0) {
          console.error(`Break with id ${breakId} not found in staged data`)
          return currentStagedData
        }

        newData.splice(breakIndex, 1)

        let allScheduleItems = newData.filter((item) => !item.isOutOfTurn || item.order > 0)
        allScheduleItems = sortByOrder(allScheduleItems)

        allScheduleItems.forEach((item, index) => {
          const itemIndex = newData.findIndex((r) => r.id === item.id)
          if (itemIndex >= 0) {
            newData[itemIndex] = { ...newData[itemIndex], order: index + 1 }
          }
        })

        const firstRider = newData.find(
          (item) => !item.isBreakDrag && !item.isOutOfTurn && item.order === 1
        )

        if (firstRider?.rideTime) {
          return recalculateAllRideTimes(newData, firstRider.rideTime)
        }

        return newData
      })
    },
    [isEditMode]
  )

  const updateOrderOfGoScratchStaged = useCallback(
    (rowId: string, isScratched: boolean) => {
      if (!isEditMode) return

      setStagedData((prevData) => {
        let newData = prevData.map((item) =>
          item.id === rowId ? { ...item, orderOfGoScratched: isScratched } : item
        )

        let count = 0
        newData = newData.map((item) => {
          if (item.orderOfGoScratched) {
            return { ...item, order: 0 }
          } else {
            if (!item.isOutOfTurn) count++
            return { ...item, order: count }
          }
        })

        newData = getNewData(newData).filter((item) => !item.isBreakDrag)
        return newData
      })
    },
    [isEditMode]
  )

  const updateScratchStaged = useCallback(
    (rowId: string) => {
      if (!isEditMode) return

      setStagedData((prevData) => {
        let newData = prevData.map((item) =>
          item.id === rowId ? { ...item, isScratched: true } : item
        )
        newData = getNewData(newData).filter((item) => !item.isBreakDrag)
        return newData
      })
    },
    [isEditMode]
  )

  const updateBreaks = useCallback(
    async (breaks: IBreak[], eventId: string) => {
      if (!currentEvent.Event) {
        console.error('Current event is null - cannot update breaks')
        return
      }

      dispatch(
        setSelectedEventKey({
          key: 'Event',
          value: { ...currentEvent.Event, breaks },
        })
      )

      await FirestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.EVENTS.NAME,
        eventId,
        {
          breaks,
        }
      )
    },
    [currentEvent.Event, dispatch]
  )

  const handleDragStart = useCallback(() => {
    setIsDragging(true)
  }, [])

  const getNewData = (data: IBreakRegistrationByDay[]) => {
    const inTurnItems = data.filter(
      (item) =>
        !item.orderOfGoScratched &&
        // && !item.isScratched
        !item.isOutOfTurn
    )

    inTurnItems.forEach((item, index) => {
      const itemIndex = data.findIndex((r) => r.id === item.id)
      if (itemIndex >= 0) {
        data[itemIndex] = { ...data[itemIndex], order: index + 1 }
      }
    })

    const firstRider = data.find(
      (item) =>
        !item.isBreakDrag &&
        !item.isOutOfTurn &&
        !item.orderOfGoScratched &&
        // !item.isScratched &&
        item.order === 1
    )

    if (firstRider?.rideTime) {
      return recalculateAllRideTimes(sortByOutOfTurn(data), firstRider.rideTime)
    } else {
      return sortByOutOfTurn(data)
    }
  }

  const handleDragEndStaged = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false)

      if (!isEditMode) return

      const { active, over } = event
      if (!over) return

      const activeId = String(active.id)
      const overId = String(over.id)

      setStagedData((currentStagedData) => {
        // find dragged item
        const draggedItem = currentStagedData.find((item) => item.id === activeId)
        if (!draggedItem) return currentStagedData

        const newData = [...currentStagedData]
        // handle dropping on the out-of-turn-dropzone (header dropzone)
        if (overId === 'out-of-turn-dropzone' && !draggedItem.isBreakDrag) {
          const itemIndex = newData.findIndex((item) => item.id === activeId)

          if (itemIndex >= 0) {
            newData[itemIndex] = {
              ...newData[itemIndex],
              isOutOfTurn: true,
              order: 0,
            }
            return getNewData(newData)
          }
        } else if (
          // handle dropping an out of turn rider back into the regular order
          draggedItem.isOutOfTurn &&
          !draggedItem.isBreakDrag &&
          overId !== 'out-of-turn-dropzone'
        ) {
          const targetIndex = newData.findIndex((item) => item.id === overId)
          if (targetIndex >= 0) {
            const targetItem = newData[targetIndex]

            if (!targetItem.isOutOfTurn) {
              const draggedIndex = newData.findIndex((item) => item.id === activeId)

              // remove the dragged item from its current pos
              const [movedItem] = newData.splice(draggedIndex, 1)

              // find target new position after removal
              const newTargetIndex = newData.findIndex((item) => item.id === overId)

              // insert after target
              newData.splice(newTargetIndex + 1, 0, {
                ...movedItem,
                isOutOfTurn: false,
              })
              return getNewData(newData)
            }
          }
        } else if (
          // handle reordering within out of turn riders
          draggedItem.isOutOfTurn &&
          !draggedItem.isBreakDrag &&
          overId !== 'out-of-turn-dropzone'
        ) {
          const targetItem = newData.find((item) => item.id === overId)
          if (targetItem && targetItem.isOutOfTurn) {
            const oldIndex = newData.findIndex((item) => item.id === activeId)
            const newIndex = newData.findIndex((item) => item.id === overId)

            if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
              const [movedItem] = newData.splice(oldIndex, 1)
              newData.splice(newIndex, 0, movedItem)
              return newData
            }
          }
        } else if (
          // handle reordering of breaks and in-turn riders
          (!draggedItem.isOutOfTurn || draggedItem.isBreakDrag) &&
          overId !== 'out-of-turn-dropzone'
        ) {
          const targetItem = newData.find((item) => item.id === overId)

          if (targetItem && (!targetItem.isOutOfTurn || targetItem.isBreakDrag)) {
            const oldIndex = newData.findIndex((item) => item.id === activeId)
            const newIndex = newData.findIndex((item) => item.id === overId)

            if (oldIndex === -1 || newIndex === -1) return currentStagedData
            if (oldIndex === newIndex) return currentStagedData

            // temp move the item to check final position
            const [movedItem] = newData.splice(oldIndex, 1)
            newData.splice(newIndex, 0, movedItem)

            // if item is a break - check if it would end up in first position
            if (draggedItem.isBreakDrag) {
              const inTurnItems = newData.filter(
                (item) =>
                  item.isBreakDrag || // include all breaks
                  (!item.isBreakDrag && !item.isOutOfTurn) // include all non-out-of-turn riders
              )

              // check if the break would be first in the ordered list
              const breakWouldBeFirst =
                inTurnItems.findIndex((item) => item.id === draggedItem.id) === 0

              if (breakWouldBeFirst) {
                // revert the move - break cannot be first
                newData.splice(newIndex, 1)
                newData.splice(oldIndex, 0, movedItem)
                return currentStagedData
              }
            }
            return getNewData(newData)
          }
        }

        return currentStagedData // no action taken - unchanged data
      })
    },
    [isEditMode]
  )

  const addBreakStaged = useCallback(() => {
    if (!isEditMode) return

    setStagedData((currentStagedData) => {
      // always find the max order from in turn riders only
      const inTurnItems = currentStagedData.filter((item) => item.isBreakDrag || !item.isOutOfTurn)

      const maxInTurnOrder = Math.max(0, ...inTurnItems.map((item) => item.order || 0))

      const dataToCreate = {
        id: `${new Date().getTime()}`,
        order: maxInTurnOrder + 1,
        time: '00:00:00',
        isBreakDrag: true,
        orderOfGoScratched: false,
        groupKey: selectedDay.value,
      }

      // add the break to the end - no recalc needed since time is 00:00:00 and break added at end
      return [...currentStagedData, dataToCreate] as IBreakRegistrationByDay[]
    })
  }, [isEditMode, selectedDay.value])

  const saveStagedChanges = useCallback(async () => {
    if (!isEditMode || stagedData.length === 0 || isSaving) return

    // check if actually any changes to save w/ lodash deep equality - prevent db calls if no changes
    if (isEqual(originalData, stagedData)) {
      onToggleEditMode()
      setStagedData([])
      toastFunctions.info({ message: MESSAGES_CONST.NO_CHANGES })
      return
    }

    setIsSaving(true)
    try {
      let count = 0
      const stagedDataUpdated = stagedData.map((item) => {
        if (item.orderOfGoScratched) {
          return { ...item, order: 0 }
        } else {
          if (!item.isOutOfTurn) count++
          return { ...item, order: count }
        }
      })

      // separate breaks from registrations
      const breaksForCurrentGroup = stagedDataUpdated.filter((item) => item.isBreakDrag)
      let registrationsByDayToUpdate = stagedDataUpdated.filter((item) => !item.isBreakDrag)

      if (!currentEvent.Event) {
        throw new Error('Current event is null, cannot update breaks')
      }

      const allExistingBreaks = currentEvent.Event.breaks || []

      // remove breaks for the current group
      const breaksFromOtherGroups = allExistingBreaks.filter(
        (breakItem) => breakItem.groupKey !== selectedDay.value
      )

      // combine breaks from other groups with new breaks for current group
      const allBreaksToSave = [
        ...breaksFromOtherGroups,
        ...breaksForCurrentGroup.map((breakItem) => ({
          id: breakItem.id,
          time: breakItem.time,
          isBreakDrag: breakItem.isBreakDrag,
          groupKey: breakItem.groupKey,
          order: breakItem.order,
          orderOfGoScratched: breakItem.orderOfGoScratched,
        })),
      ]

      await updateBreaks(allBreaksToSave, eventId)

      // Update firestore collection on BE by batch
      if (registrationsByDayToUpdate?.[0]) {
        await httpService({
          url: 'update_registrations_by_day',
          method: 'POST',
          data: {
            registrationsByDay: registrationsByDayToUpdate,
          },
        })
      }

      setRegistrationsByDay(
        registrationsByDay?.map((registrationByDay) => {
          const updatedRegistration = stagedDataUpdated.find(
            (current) => current.id === registrationByDay.id && !current.isBreakDrag
          )
          if (updatedRegistration) {
            return updatedRegistration as IRegistrationByDayInterface
          }
          return registrationByDay
        }) ?? []
      )

      // exit edit mode, clear staged data only if all success
      onToggleEditMode()
      setStagedData([])

      toastFunctions.success({ message: MESSAGES_CONST.EVENT_ORDER_UPDATED })
    } catch (error) {
      console.error('Error saving staged changes:', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setIsSaving(false)
    }
  }, [
    isEditMode,
    stagedData,
    originalData,
    eventId,
    updateBreaks,
    registrationsGroupedByDay,
    selectedDay.value,
    setRegistrationsByDay,
    registrationsByDay,
    onToggleEditMode,
    toastFunctions,
    currentEvent.Event,
    isSaving,
  ])

  const columns = React.useMemo<ColumnDef<IBreakRegistrationByDay>[]>(() => {
    return getRiderColumns({
      eventTeamMembers,
      eventTeamMembersLoading,
      updateFirstRiderTimeStaged,
      updateOutOfTurnRiderTimeStaged,
      updateBreakTimeStaged,
      updateOrderOfGoScratchStaged,
      updateScratchStaged,
      deleteBreakStaged,
      isEditMode,
      onToggleEditMode: onToggleEditMode,
      isSaving,
    })
  }, [
    eventTeamMembers,
    eventTeamMembersLoading,
    isEditMode,
    updateOutOfTurnRiderTimeStaged,
    updateFirstRiderTimeStaged,
    updateBreakTimeStaged,
    updateOrderOfGoScratchStaged,
    updateScratchStaged,
    deleteBreakStaged,
    isSaving,
    onToggleEditMode,
  ])

  const table = useReactTable({
    data: registrationsToDisplay,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id, // required because row indexes will change
    meta: {
      clashedIds,
      saveStagedChanges,
    },
  })

  const filteredRows = table.getFilteredRowModel().rows.filter((item) => {
    if (!searchValue?.trim()) return true
    const searchLower = searchValue.toLowerCase()
    const riderName = item.original.riderName?.toLowerCase() || ''
    return riderName.includes(searchLower)
  })
  const sortableRowIds = filteredRows.map((row) => row.id)

  return registrationsToDisplay.length > 0 ? (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEndStaged}>
      <table className={'w-full'}>
        <thead>
          {table.getHeaderGroups().map((headerGroup, index) => (
            <OrderOfGoRiderTableHeader
              key={index}
              headerGroup={headerGroup}
              isEditMode={isEditMode}
              isDragging={isDragging}
            />
          ))}
        </thead>
        <tbody>
          <SortableContext items={sortableRowIds} strategy={verticalListSortingStrategy}>
            {filteredRows.map((row, rowIndex) => (
              <DraggableRiderRow
                key={row.id}
                tableRow={row}
                isLastChild={rowIndex === filteredRows.length - 1}
                isEditMode={isEditMode}
                hasClash={hasClash(
                  row.original.horsesSelected?.map(({ id }) => id) ?? [],
                  clashedIds
                )}
              />
            ))}
          </SortableContext>
          {isEditMode && <AddBreak addBreak={addBreakStaged} />}
        </tbody>
      </table>
    </DndContext>
  ) : (
    <div className="flex justify-center p-6">There are no records to display</div>
  )
}
